import { memo, useEffect, useRef, useState } from "react";
import { useAppSelector } from "../../store/hooks";
import { selectCurrentLang } from "../../store/selectors/appSelectors";
import { useAuth } from "../../utils/hooks/useAuth";
import useGlobalNotification from "../../utils/hooks/useGlobalNotification";
import GlobalNotificationCampaignContent from "../GlobalNotificationCampaignBanner/components/GlobalNotificationCampaignContent";
import { isLanguageMatched } from "../../features/NewPrices2024/utils/notificationHelpers";

import styles from "./styles.module.scss";

/**
 * Mobile strip for campaign notification.
 * Shown only on screens < 992px, above the header.
 */
const MobileCampaignStrip = memo(() => {
  const { currentUser } = useAuth();
  const { activeCampaignNotification } = useGlobalNotification();
  const currentLang = useAppSelector(selectCurrentLang);
  const stripRef = useRef<HTMLDivElement | null>(null);
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth < 992 : false
  );
  const [isVisible, setIsVisible] = useState(true);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 992);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Restore visibility state from sessionStorage (same key as sidebar banner)
  useEffect(() => {
    if (!currentUser?.id || !activeCampaignNotification) {
      return;
    }

    const sessionKey = `campaign-banner-closed-${activeCampaignNotification.TitleConst}-${currentUser.id}`;
    const isClosed = sessionStorage.getItem(sessionKey);

    if (isClosed === "true") {
      setIsVisible(false);
    }
  }, [currentUser?.id, activeCampaignNotification]);

  // Manage body class and CSS variable for dynamic strip height
  useEffect(() => {
    const shouldShowStrip =
      isMobile &&
      !!currentUser &&
      !!activeCampaignNotification &&
      !!activeCampaignNotification.Languages &&
      isLanguageMatched(currentLang, activeCampaignNotification.Languages) &&
      isVisible;

    if (shouldShowStrip && stripRef.current) {
      const height = stripRef.current.offsetHeight;
      document.documentElement.style.setProperty(
        "--mobile-strip-height",
        `${height}px`
      );
      document.body.classList.add("has-mobile-campaign-strip");
    } else {
      document.documentElement.style.removeProperty("--mobile-strip-height");
      document.body.classList.remove("has-mobile-campaign-strip");
    }

    return () => {
      document.documentElement.style.removeProperty("--mobile-strip-height");
      document.body.classList.remove("has-mobile-campaign-strip");
    };
  }, [isMobile, currentUser, activeCampaignNotification, currentLang, isVisible]);

  // Show only for logged in users, matching language, mobile width and active campaign
  // Languages is required field, so it will always be present
  if (
    !isMobile ||
    !currentUser ||
    !activeCampaignNotification ||
    !activeCampaignNotification.Languages ||
    !isLanguageMatched(currentLang, activeCampaignNotification.Languages) ||
    !isVisible
  ) {
    return null;
  }

  const handleClose = () => {
    if (currentUser?.id && activeCampaignNotification) {
      const sessionKey = `campaign-banner-closed-${activeCampaignNotification.TitleConst}-${currentUser.id}`;
      sessionStorage.setItem(sessionKey, "true");
    }
    setIsVisible(false);
  };

  return (
    <div ref={stripRef} className={styles.stripWrapper} data-no-print>
      <GlobalNotificationCampaignContent
        notification={activeCampaignNotification}
        isMobileStrip
        onClose={handleClose}
      />
    </div>
  );
});

export default MobileCampaignStrip;


